// REMIX HMR BEGIN
if (!window.$RefreshReg$ || !window.$RefreshSig$ || !window.$RefreshRuntime$) {
  console.warn('remix:hmr: React Fast Refresh only works when the Remix compiler is running in development mode.');
} else {
  var prevRefreshReg = window.$RefreshReg$;
  var prevRefreshSig = window.$RefreshSig$;
  window.$RefreshReg$ = (type, id) => {
    window.$RefreshRuntime$.register(type, "\"app/root.tsx\"" + id);
  }
  window.$RefreshSig$ = window.$RefreshRuntime$.createSignatureFunctionForTransform;
}
var _s = $RefreshSig$(),
  _s2 = $RefreshSig$(),
  _s3 = $RefreshSig$(),
  _s4 = $RefreshSig$(),
  _s5 = $RefreshSig$();
import * as __hmr__ from "remix:hmr";
if (import.meta) {
  import.meta.hot = __hmr__.createHotContext(
  //@ts-expect-error
  "app/root.tsx");
}
// REMIX HMR END

import React, { createContext, useContext, useEffect, useState } from 'react';
import { json, redirectDocument } from '@remix-run/node';
import { Links, LiveReload, Meta, Outlet, Scripts, ScrollRestoration, useLoaderData, useNavigation, useRouteLoaderData } from '@remix-run/react';
import { hydrateOrg, optionalOrg, requireUser, toClientUser } from './services/session.server.ts';
import tailwind from './tailwind.css';
import { Header } from './components/header.tsx';
import { PageActionsProvider } from './components/page-actions.tsx';
import { Button } from './components/ui/button.tsx';
import { RefreshCw } from 'lucide-react';
import { getEnv } from './services/env.server.ts';
import { GeneralErrorBoundary } from './components/error-boundary.tsx';
import rdtStylesheet from 'remix-development-tools/index.css';
import { getPatientPrograms } from './services/patient.service.ts';
import { useSessionRefresh } from './hooks/useSessionRefresh.ts';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle } from './components/ui/alert-dialog.tsx';
import { getServerTiming } from './services/timing.server.ts';
import { userHasPermission, userHasRole } from './utils.ts';
import { datadogRum } from '@datadog/browser-rum';
const _links = [{
  rel: 'preconnect',
  href: 'https://fonts.googleapis.com'
}, {
  rel: 'preconnect',
  href: 'https://fonts.gstatic.com',
  crossOrigin: 'anonymous'
}, {
  rel: 'stylesheet',
  href: 'https://fonts.googleapis.com/css2?family=Roboto:wght@100;300;400;500;700;900&display=swap'
}, {
  rel: 'stylesheet',
  href: tailwind
}];
if (process.env.NODE_ENV === 'development') {
  _links.push({
    rel: 'stylesheet',
    href: rdtStylesheet
  });
}
export const links = () => _links;
export const meta = () => {
  return [{
    charset: 'utf-8'
  }, {
    title: 'Nexus'
  }, {
    viewport: 'width=device-width,initial-scale=1'
  }];
};
export const shouldRevalidate = ({
  currentUrl,
  formMethod,
  nextUrl
}) => {
  return formMethod == 'POST' || nextUrl.pathname == '/login' || currentUrl.href == nextUrl.href;
};
export const loader = async ({
  request
}) => {
  // @todo don't reload every sub page
  const url = new URL(request.url);
  if (url.hostname == 'beta.encorenexus.com') {
    const redirect_url = `https://app.encorenexus.com${url.pathname}${url.search}`;
    return redirectDocument(redirect_url);
  }
  if (process.env.MAINTENANCE_MODE === '1') {
    return json({
      maintenance: true
    });
  }
  if (url.pathname.includes('/login/sso/')) {
    const org = url.pathname.split('/')[3];
    const redirect_url = `https://id.encorenexus.com/saml/drupal_login/${org}?returnTo=/app`;
    return redirectDocument(redirect_url);
  }
  const {
    time,
    getServerTimingHeader
  } = getServerTiming();
  let user = null;
  if (!['/login', '/logout', '/oauth/callback'].includes(url.pathname)) {
    user = await time('root#requireUser', async () => await requireUser(request));
  }
  const org = hydrateOrg(user, await optionalOrg(request));
  let programs = [];
  if (user) {
    programs = await time('root#getPatientPrograms', async () => await getPatientPrograms(request));
  }
  return json({
    user: user ? toClientUser(user) : null,
    org: org,
    ENV: getEnv(),
    programs: programs
  }, {
    headers: getServerTimingHeader()
  });
};
export function useRootLoaderData() {
  _s();
  return useRouteLoaderData('root');
}
_s(useRootLoaderData, "HP+Y84T8RxtBxKrBpCtBHZ7kTOE=", false, function () {
  return [useRouteLoaderData];
});
export const CensorContext = createContext(undefined);
function Document({
  children,
  env = {}
}) {
  _s2();
  const [censor, setCensor] = useState(false);
  const navigation = useNavigation();
  return <html lang="en">
			<head>
				<Meta />
				<Links />
			</head>
			<body className="font-roboto bg-slate-200 relative">
				<CensorContext.Provider value={{
        censor,
        setCensor
      }}>
					{children}
				</CensorContext.Provider>

				<AlertDialog open={navigation.state == 'loading'}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle className="flex gap-2 items-center justify-center">
								<RefreshCw width={32} height={32} className="animate-spin" />

								<span className="text-2xl">Loading...</span>
							</AlertDialogTitle>
						</AlertDialogHeader>
					</AlertDialogContent>
				</AlertDialog>

				<script dangerouslySetInnerHTML={{
        __html: `window.ENV = ${JSON.stringify(env)}`
      }} />

				<ScrollRestoration />
				<Scripts />
				<LiveReload />
			</body>
		</html>;
}
_s2(Document, "7hd5WDne53UI4mniF5OAwbNYyns=", false, function () {
  return [useNavigation];
});
_c = Document;
function AdminTools() {
  _s3();
  const {
    censor,
    setCensor
  } = useContext(CensorContext);
  return <div className="p-4 flex gap-2 text-sm content-center items-center print:hidden">
		<div className="font-bold">admin tools:</div>
		{userHasPermission('content.censor') ? <Button variant="link" onClick={() => setCensor(!censor)} className="">{censor ? "show" : "hide"} phi</Button> : null}
	</div>;
}
_s3(AdminTools, "gDsCjeeItUuvgOWf1v4qoK9RF6k=");
_c2 = AdminTools;
function MaintenancePage() {
  return <Document>
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-center">
					<img src="/images/nexus-logo.svg" alt="Nexus" className="mx-auto mb-8 h-16" />

					<h1 className="mb-4 text-2xl font-bold text-slate-800">
						Scheduled Maintenance
					</h1>
					<p className="text-lg text-slate-600">
						Nexus is currently undergoing scheduled maintenance.
						<br />
						We&apos;ll be back shortly. Please check back soon.
					</p>
				</div>
			</div>
		</Document>;
}
_c3 = MaintenancePage;
function AppContent({
  user,
  org,
  ENV
}) {
  _s4();
  useSessionRefresh(user?.expires);
  useEffect(() => {
    if (user) {
      datadogRum.setUser({
        id: user.sub,
        uuid: user.uuid,
        name: user.name,
        email: user.email,
        roles: user.roles
      });
    }
    if (org) {
      datadogRum.setGlobalContextProperty('org', org);
    }
  }, [user, org]);
  return <Document env={ENV}>
			<PageActionsProvider>
				{user && <Header />}
				<div id="root" className="overflow-x-hidden">
					<Outlet />
				</div>
				{userHasRole(user, 'administrator') ? <AdminTools /> : null}
			</PageActionsProvider>
		</Document>;
}
_s4(AppContent, "rlOSr5XXfbnqBorrAXjmmIm3iOI=", false, function () {
  return [useSessionRefresh];
});
_c4 = AppContent;
export function App() {
  _s5();
  const data = useLoaderData();
  if (data?.maintenance) {
    return <MaintenancePage />;
  }
  return <AppContent user={data.user} org={data.org} ENV={data.ENV} />;
}
_s5(App, "5thj+e1edPyRpKif1JmVRC6KArE=", false, function () {
  return [useLoaderData];
});
_c5 = App;
export function ErrorBoundary() {
  return <Document>
			<GeneralErrorBoundary />
		</Document>;
}
_c6 = ErrorBoundary;
let AppExport = App;
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const {
    withDevTools
  } = require('remix-development-tools');
  AppExport = withDevTools(AppExport);
}
export { headers } from '~/services/defaults.server';
export default AppExport;
var _c, _c2, _c3, _c4, _c5, _c6;
$RefreshReg$(_c, "Document");
$RefreshReg$(_c2, "AdminTools");
$RefreshReg$(_c3, "MaintenancePage");
$RefreshReg$(_c4, "AppContent");
$RefreshReg$(_c5, "App");
$RefreshReg$(_c6, "ErrorBoundary");

window.$RefreshReg$ = prevRefreshReg;
window.$RefreshSig$ = prevRefreshSig;